import { Box, CircularProgress, IconButton, Paper, Typography } from "@mui/material"
import { ArrowDownwardOutlined, ChevronLeftOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useParams, useRouter } from "@tanstack/react-router"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { useAffectedCustomerColumns } from "../../hooks/useAffectedCustomerColumns"
import { useGetProduct } from "../../hooks/useGetProduct"

import { useSubmitSourceChange } from "./hooks/useSubmitSourceChange"

import { Button } from "@/components/common/Button"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { PriorityStatus } from "@/graphql/codegen/graphql"
import { SourceProductHeader } from "@/screens/Products/components/SourceChanges/components/SourceProductHeader"
import { useGetAffectedCustomers } from "@/screens/Products/components/SourceChanges/hooks/useGetAffectedCustomers"

export const SourceChangeReviewPage: FC = () => {
  const { productId, sourceId } = useParams({
    from: "/app/product/$productId/source/$sourceId/",
  })
  const router = useRouter()
  const navigate = useNavigate()

  const { productDetails: product, productDetailsFetching: isFetchingProduct } = useGetProduct(Number(productId))

  const { productDetails: targetProduct, productDetailsFetching: isFetchingTargetProduct } = useGetProduct(
    Number(sourceId)
  )

  const { affectedCustomers } = useGetAffectedCustomers(Number(productId))
  const { submitSourceChange, isSubmitting } = useSubmitSourceChange()
  const [priorityOverrides, setPriorityOverrides] = useState<Record<string, PriorityStatus>>({})

  const priorities = useMemo(
    () => ({
      ...Object.fromEntries(
        affectedCustomers.map((customer) => [customer.companyProductAssociationLocationId, customer.priorityStatus])
      ),
      ...priorityOverrides,
    }),
    [affectedCustomers, priorityOverrides]
  )

  const handlePriorityChange = (id: string, priority: PriorityStatus) => {
    setPriorityOverrides((prev) => ({ ...prev, [id]: priority }))
  }

  const columns = useAffectedCustomerColumns({ priorities, onPriorityChange: handlePriorityChange })

  const handleBack = () => {
    router.history.back()
  }

  const handleSourceChangeSubmit = async () => {
    const isSuccess = await submitSourceChange({
      originalProductId: Number(productId),
      newSourceProductId: Number(sourceId),
      items: Object.entries(priorities).map(([companyProductAssociationLocationId, newSourcePriority]) => ({
        companyProductAssociationLocationId,
        priorityStatus: newSourcePriority,
      })),
    })

    if (!isSuccess) return

    await navigate({
      to: "/app/product/$productId/edit/information",
      params: { productId },
      replace: true,
    })
  }

  if (!product || !targetProduct || isFetchingProduct || isFetchingTargetProduct) {
    return (
      <Box className='flex w-full items-center justify-center'>
        <CircularProgress size={48} color='primary' />
      </Box>
    )
  }

  return (
    <Box className='w-full px-6 pb-6'>
      <Paper elevation={0} className='flex flex-col bg-white'>
        <Box className='flex items-center justify-between'>
          <Box className='flex items-center gap-4'>
            <IconButton aria-label='back' className='rounded bg-gray-200 text-primary' onClick={handleBack}>
              <ChevronLeftOutlined />
            </IconButton>
            <Typography variant='h5' color='primary' className='font-normal'>
              Review Source Changes
            </Typography>
          </Box>
          <Button
            onClick={handleSourceChangeSubmit}
            disabled={isSubmitting}
            data-testid='source-change.page.submit-button'
          >
            Submit Source Changes
          </Button>
        </Box>

        <Box className='mt-6'>
          <Box className='rounded-md border border-solid border-gray-400'>
            <SourceProductHeader product={product} />
          </Box>

          <ArrowDownwardOutlined className='my-4 flex size-10 w-full justify-center text-gray-600' />

          <Box className='overflow-hidden rounded-md border border-solid border-gray-400'>
            <SourceProductHeader product={targetProduct} />
            <AerosBaseTable
              features={{
                tanstackOptions: {
                  data: affectedCustomers,
                  columns,
                  getCoreRowModel: getCoreRowModel(),
                  getRowId: (row) => row.companyProductAssociationLocationId,
                },
              }}
              noDataMessage='No affected customers'
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
